import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  LogOut, Clock, CheckCircle, XCircle, AlertTriangle, Send, 
  UserCheck, Building, ShieldCheck, DollarSign, RotateCcw, 
  Calendar, MessageSquare, User, Briefcase, Video, Phone, MapPin,
  Laptop, CreditCard, Key, FileText, ClipboardList, History
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExitPortalPage = ({ user, setUser }) => {
  const [activeRequest, setActiveRequest] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [showInterviewDialog, setShowInterviewDialog] = useState(false);
  const [showClearanceDialog, setShowClearanceDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [selectedDept, setSelectedDept] = useState(null);
  const [clearanceItems, setClearanceItems] = useState([]);
  const [clearanceNotes, setClearanceNotes] = useState('');
  const [userDept, setUserDept] = useState('');
  
  const [interviewForm, setInterviewForm] = useState({
    interview_date: '',
    interview_time: '',
    interview_mode: '',
    interview_location: ''
  });
  
  const [interviewFeedback, setInterviewFeedback] = useState('');
  
  const [exitForm, setExitForm] = useState({
    reason_for_leaving: '',
    detailed_reason: ''
  });

  const reasonOptions = [
    'Better Opportunity',
    'Career Growth',
    'Higher Education',
    'Personal Reasons',
    'Relocation',
    'Health Issues',
    'Family Commitments',
    'Compensation',
    'Work Environment',
    'Other'
  ];

  useEffect(() => {
    fetchData();
    fetchUserDept();
  }, []);

  const fetchUserDept = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/employees/me`, config);
      setUserDept(response.data?.department?.toLowerCase() || '');
    } catch (error) {
      console.error('Failed to fetch user dept:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [activeRes, historyRes, pendingRes] = await Promise.all([
        axios.get(`${API}/exit/my-request`, config).catch(() => ({ data: null })),
        axios.get(`${API}/exit/my-requests`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/exit/pending-approvals`, config).catch(() => ({ data: [] }))
      ]);

      setActiveRequest(activeRes.data);
      setMyRequests(historyRes.data || []);
      setPendingApprovals(pendingRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!exitForm.reason_for_leaving) {
      toast.error('Please select a reason for leaving');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/exit/request`, exitForm, config);
      
      toast.success('Exit request submitted successfully');
      setShowSubmitDialog(false);
      setExitForm({ reason_for_leaving: '', detailed_reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit exit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (action) => {
    if (!selectedRequest) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/exit/approve`, {
        request_id: selectedRequest.id,
        action: action,
        notes: approvalNotes
      }, config);

      toast.success(`Exit request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowApprovalDialog(false);
      setSelectedRequest(null);
      setApprovalNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const handleRevoke = async () => {
    if (!selectedRequest || !revokeReason) {
      toast.error('Please provide a reason for revocation');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/exit/revoke`, {
        request_id: selectedRequest.id,
        reason: revokeReason
      }, config);

      toast.success('Exit request revoked successfully');
      setShowRevokeDialog(false);
      setSelectedRequest(null);
      setRevokeReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke exit request');
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewForm.interview_date || !interviewForm.interview_time || !interviewForm.interview_mode) {
      toast.error('Please fill in all required interview details');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/exit/schedule-interview`, {
        request_id: selectedRequest.id,
        ...interviewForm
      }, config);

      toast.success('Exit interview scheduled successfully');
      setShowInterviewDialog(false);
      setSelectedRequest(null);
      setInterviewForm({ interview_date: '', interview_time: '', interview_mode: '', interview_location: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule interview');
    }
  };

  const handleCompleteInterview = async () => {
    if (!interviewFeedback) {
      toast.error('Please provide interview feedback');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/exit/complete-interview`, {
        request_id: selectedRequest.id,
        feedback: interviewFeedback
      }, config);

      toast.success('Exit interview marked as complete');
      setShowInterviewDialog(false);
      setSelectedRequest(null);
      setInterviewFeedback('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete interview');
    }
  };

  const handleUpdateClearance = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/exit/clearance/update`, {
        request_id: selectedRequest.id,
        department: selectedDept,
        items: clearanceItems,
        notes: clearanceNotes
      }, config);

      toast.success(`${selectedDept.toUpperCase()} clearance updated`);
      setShowClearanceDialog(false);
      setSelectedRequest(null);
      setSelectedDept(null);
      setClearanceItems([]);
      setClearanceNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update clearance');
    }
  };

  const openClearanceDialog = (request, dept) => {
    setSelectedRequest(request);
    setSelectedDept(dept);
    setClearanceItems([...(request.clearance?.[dept]?.items || [])]);
    setClearanceNotes(request.clearance?.[dept]?.notes || '');
    setShowClearanceDialog(true);
  };

  // Check if user can update a specific department's clearance
  const canUpdateClearance = (dept) => {
    if (dept === 'manager') return pendingApprovals.some(p => p.approval_role === 'rm');
    if (dept === 'hr') return isHR;
    if (dept === 'it') return userDept === 'it';
    if (dept === 'finance') return userDept === 'finance' || userDept === 'payroll';
    if (dept === 'admin') return userDept === 'admin' || userDept === 'administration';
    return false;
  };

  // Check if all clearance items for a department are filled (for enabling approve button)
  const isClearanceComplete = (request, dept) => {
    const items = request?.clearance?.[dept]?.items || [];
    return items.every(item => item.status !== null);
  };

  const getClearanceProgress = (clearance) => {
    if (!clearance) return { completed: 0, total: 0, percentage: 0 };
    let total = 0;
    let completed = 0;
    Object.values(clearance).forEach(dept => {
      dept.items?.forEach(item => {
        total++;
        if (item.status !== null) completed++;
      });
    });
    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  const deptConfig = {
    it: { label: 'IT', icon: Laptop, color: 'blue' },
    finance: { label: 'Finance', icon: CreditCard, color: 'green' },
    admin: { label: 'Administration', icon: Key, color: 'purple' },
    hr: { label: 'HR', icon: FileText, color: 'orange' },
    manager: { label: 'Manager', icon: UserCheck, color: 'indigo' }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getApprovalStatusBadge = (status) => {
    const configs = {
      'pending': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', icon: Clock, label: 'Pending' },
      'approved': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', icon: CheckCircle, label: 'Approved' },
      'rejected': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', icon: XCircle, label: 'Rejected' }
    };
    const config = configs[status] || configs['pending'];
    const IconComponent = config.icon;
    
    return (
      <Badge variant="outline" className={`${config.bg} ${config.text} ${config.border} text-xs`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getOverallStatusBadge = (request) => {
    if (request.is_revoked) {
      return <Badge className="bg-gray-500">Revoked</Badge>;
    }
    if (request.status === 'rejected') {
      return <Badge className="bg-red-600">Rejected</Badge>;
    }
    if (request.status === 'completed') {
      return <Badge className="bg-green-600">Completed</Badge>;
    }
    return <Badge className="bg-orange-500">Pending Approvals</Badge>;
  };

  // Check if current user is HR (can revoke) - NOT admin role
  const isHR = userDept === 'hr' || user.email?.toLowerCase().startsWith('hr') || 
               pendingApprovals.some(p => p.approval_role === 'hr');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header user={user} setUser={setUser} />
        <Navigation user={user} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="exit-portal-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <main className="container mx-auto px-4 py-6 max-w-screen-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LogOut className="w-6 h-6 text-red-600" />
            Exit Portal
          </h1>
          <p className="text-gray-600 mt-1">Submit and track your resignation request</p>
        </div>

        <Tabs defaultValue="my-request" className="space-y-6">
          <TabsList>
            <TabsTrigger value="my-request">My Request</TabsTrigger>
            <TabsTrigger value="history">
              My History ({myRequests.length})
            </TabsTrigger>
            {pendingApprovals.length > 0 && (
              <TabsTrigger value="approvals" className="text-orange-600">
                Pending Approvals ({pendingApprovals.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* My Active Request Tab */}
          <TabsContent value="my-request">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                {activeRequest ? (
                  <Card className="shadow-lg border-l-4 border-l-red-500" data-testid="my-exit-request">
                    <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <LogOut className="w-5 h-5 text-red-600" />
                          My Exit Request
                        </span>
                        {getOverallStatusBadge(activeRequest)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      {activeRequest.is_revoked ? (
                        <div className="bg-gray-100 p-4 rounded-lg text-center">
                          <RotateCcw className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-600 font-medium">Your exit request has been revoked</p>
                          <p className="text-sm text-gray-500 mt-1">Reason: {activeRequest.revocation_reason}</p>
                          <p className="text-sm text-gray-500">Revoked on: {formatDate(activeRequest.revoked_at)}</p>
                        </div>
                      ) : (
                        <>
                          {/* Request Details */}
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs text-gray-500 uppercase">Submission Date</p>
                              <p className="font-semibold flex items-center gap-1">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                {formatDate(activeRequest.submission_date)}
                              </p>
                            </div>
                            <div className="p-3 bg-red-50 rounded-lg">
                              <p className="text-xs text-gray-500 uppercase">Last Working Day</p>
                              <p className="font-semibold text-red-700 flex items-center gap-1">
                                <Calendar className="w-4 h-4 text-red-400" />
                                {formatDate(activeRequest.last_working_day)}
                              </p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                              <p className="text-xs text-gray-500 uppercase">Reason for Leaving</p>
                              <p className="font-medium">{activeRequest.reason_for_leaving}</p>
                              {activeRequest.detailed_reason && (
                                <p className="text-sm text-gray-600 mt-1">{activeRequest.detailed_reason}</p>
                              )}
                            </div>
                          </div>

                          {/* Parallel Approval Status Grid - All 5 Departments */}
                          <div className="border-t pt-4">
                            <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Department Approval Status (Parallel)
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                              {/* RM Approval */}
                              <div className={`p-3 rounded-lg border-2 ${
                                activeRequest.rm_status === 'approved' ? 'bg-green-50 border-green-300' : 
                                activeRequest.rm_status === 'rejected' ? 'bg-red-50 border-red-300' : 
                                'bg-red-50 border-red-200'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <UserCheck className={`w-4 h-4 ${
                                    activeRequest.rm_status === 'approved' ? 'text-green-600' : 
                                    activeRequest.rm_status === 'rejected' ? 'text-red-600' : 
                                    'text-red-500'
                                  }`} />
                                  <span className="text-xs font-medium">Manager</span>
                                </div>
                                {getApprovalStatusBadge(activeRequest.rm_status)}
                                {activeRequest.rm_notes && (
                                  <div className="mt-2 text-xs text-gray-600 flex items-start gap-1">
                                    <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>{activeRequest.rm_notes}</span>
                                  </div>
                                )}
                              </div>

                              {/* HR Approval */}
                              <div className={`p-3 rounded-lg border-2 ${
                                activeRequest.hr_status === 'approved' ? 'bg-green-50 border-green-300' : 
                                activeRequest.hr_status === 'rejected' ? 'bg-red-50 border-red-300' : 
                                'bg-red-50 border-red-200'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <Building className={`w-4 h-4 ${
                                    activeRequest.hr_status === 'approved' ? 'text-green-600' : 
                                    activeRequest.hr_status === 'rejected' ? 'text-red-600' : 
                                    'text-red-500'
                                  }`} />
                                  <span className="text-xs font-medium">HR</span>
                                </div>
                                {getApprovalStatusBadge(activeRequest.hr_status)}
                                {activeRequest.hr_notes && (
                                  <div className="mt-2 text-xs text-gray-600 flex items-start gap-1">
                                    <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>{activeRequest.hr_notes}</span>
                                  </div>
                                )}
                              </div>

                              {/* Admin Approval */}
                              <div className={`p-3 rounded-lg border-2 ${
                                activeRequest.admin_status === 'approved' ? 'bg-green-50 border-green-300' : 
                                activeRequest.admin_status === 'rejected' ? 'bg-red-50 border-red-300' : 
                                'bg-red-50 border-red-200'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <ShieldCheck className={`w-4 h-4 ${
                                    activeRequest.admin_status === 'approved' ? 'text-green-600' : 
                                    activeRequest.admin_status === 'rejected' ? 'text-red-600' : 
                                    'text-red-500'
                                  }`} />
                                  <span className="text-xs font-medium">Admin</span>
                                </div>
                                {getApprovalStatusBadge(activeRequest.admin_status)}
                                {activeRequest.admin_notes && (
                                  <div className="mt-2 text-xs text-gray-600 flex items-start gap-1">
                                    <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>{activeRequest.admin_notes}</span>
                                  </div>
                                )}
                              </div>

                              {/* Payroll Approval */}
                              <div className={`p-3 rounded-lg border-2 ${
                                activeRequest.payroll_status === 'approved' ? 'bg-green-50 border-green-300' : 
                                activeRequest.payroll_status === 'rejected' ? 'bg-red-50 border-red-300' : 
                                'bg-red-50 border-red-200'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <DollarSign className={`w-4 h-4 ${
                                    activeRequest.payroll_status === 'approved' ? 'text-green-600' : 
                                    activeRequest.payroll_status === 'rejected' ? 'text-red-600' : 
                                    'text-red-500'
                                  }`} />
                                  <span className="text-xs font-medium">Payroll</span>
                                </div>
                                {getApprovalStatusBadge(activeRequest.payroll_status)}
                                {activeRequest.payroll_notes && (
                                  <div className="mt-2 text-xs text-gray-600 flex items-start gap-1">
                                    <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>{activeRequest.payroll_notes}</span>
                                  </div>
                                )}
                              </div>

                              {/* IT Approval */}
                              <div className={`p-3 rounded-lg border-2 ${
                                activeRequest.it_status === 'approved' ? 'bg-green-50 border-green-300' : 
                                activeRequest.it_status === 'rejected' ? 'bg-red-50 border-red-300' : 
                                'bg-red-50 border-red-200'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <Laptop className={`w-4 h-4 ${
                                    activeRequest.it_status === 'approved' ? 'text-green-600' : 
                                    activeRequest.it_status === 'rejected' ? 'text-red-600' : 
                                    'text-red-500'
                                  }`} />
                                  <span className="text-xs font-medium">IT</span>
                                </div>
                                {getApprovalStatusBadge(activeRequest.it_status)}
                                {activeRequest.it_notes && (
                                  <div className="mt-2 text-xs text-gray-600 flex items-start gap-1">
                                    <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>{activeRequest.it_notes}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {activeRequest.status === 'completed' && (
                            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                              <p className="text-green-800 font-medium flex items-center gap-2">
                                <CheckCircle className="w-5 h-5" />
                                Your exit request has been fully approved by all departments
                              </p>
                              <p className="text-sm text-green-600 mt-1">
                                Please coordinate with HR for exit formalities and handover.
                              </p>
                            </div>
                          )}

                          {/* Exit Interview Section */}
                          <div className="border-t pt-4 mt-4">
                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <Video className="w-4 h-4" />
                              Exit Interview
                            </h4>
                            {activeRequest.interview_scheduled ? (
                              <div className={`p-4 rounded-lg ${activeRequest.interview_completed ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium">{activeRequest.interview_completed ? 'Interview Completed' : 'Interview Scheduled'}</span>
                                  <Badge className={activeRequest.interview_completed ? 'bg-green-500' : 'bg-blue-500'}>
                                    {activeRequest.interview_completed ? 'Completed' : 'Scheduled'}
                                  </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3 text-gray-400" />
                                    <span>{activeRequest.interview_date}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-3 h-3 text-gray-400" />
                                    <span>{activeRequest.interview_time}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {activeRequest.interview_mode === 'Video Call' ? <Video className="w-3 h-3 text-gray-400" /> : 
                                     activeRequest.interview_mode === 'Phone' ? <Phone className="w-3 h-3 text-gray-400" /> : 
                                     <MapPin className="w-3 h-3 text-gray-400" />}
                                    <span>{activeRequest.interview_mode}</span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                                <Video className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm">Exit interview not yet scheduled</p>
                              </div>
                            )}
                          </div>

                          {/* Clearance Checklist Section */}
                          <div className="border-t pt-4 mt-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                                <ClipboardList className="w-4 h-4" />
                                Clearance Checklist
                              </h4>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">
                                  {getClearanceProgress(activeRequest.clearance).completed}/{getClearanceProgress(activeRequest.clearance).total}
                                </span>
                                <Progress value={getClearanceProgress(activeRequest.clearance).percentage} className="w-24 h-2" />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {Object.entries(deptConfig).map(([dept, config]) => {
                                const deptClearance = activeRequest.clearance?.[dept];
                                const filledItems = deptClearance?.items?.filter(i => i.status !== null).length || 0;
                                const totalItems = deptClearance?.items?.length || 0;
                                const IconComp = config.icon;
                                
                                return (
                                  <div 
                                    key={dept}
                                    className={`p-3 rounded-lg border-2 ${
                                      deptClearance?.cleared ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <IconComp className={`w-4 h-4 ${deptClearance?.cleared ? 'text-green-600' : 'text-gray-500'}`} />
                                        <span className="text-sm font-medium">{config.label}</span>
                                      </div>
                                      <Badge className={deptClearance?.cleared ? 'bg-green-500' : 'bg-gray-400'}>
                                        {filledItems}/{totalItems}
                                      </Badge>
                                    </div>
                                    <div className="space-y-1">
                                      {deptClearance?.items?.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-xs">
                                          <span className={item.status ? 'text-gray-700' : 'text-gray-400'}>{item.name}</span>
                                          <span className={`font-medium ${
                                            item.status === 'yes' ? 'text-green-600' : 
                                            item.status === 'no' ? 'text-red-600' : 
                                            item.status === 'na' ? 'text-gray-500' : 'text-gray-300'
                                          }`}>
                                            {item.status?.toUpperCase() || '-'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="shadow-lg">
                    <CardContent className="py-12 text-center">
                      <LogOut className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No Active Exit Request</h3>
                      <p className="text-gray-500 mb-6">You have not submitted a resignation request yet.</p>
                      <Button 
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => setShowSubmitDialog(true)}
                        data-testid="submit-exit-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Submit Resignation
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right Column - Info */}
              <div>
                <Card className="shadow-lg">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                    <CardTitle className="flex items-center gap-2 text-blue-800">
                      <Clock className="w-5 h-5" />
                      Notice Period
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-4xl font-bold text-blue-600">60</p>
                      <p className="text-sm text-gray-600">Days</p>
                    </div>
                    <p className="text-sm text-gray-500 mt-3 text-center">
                      Your last working day will be calculated as 60 days from the submission date.
                    </p>
                    
                    <div className="mt-4 border-t pt-4">
                      <h5 className="font-medium text-gray-700 mb-2">Parallel Approvals Required</h5>
                      <p className="text-xs text-gray-500 mb-2">All departments approve simultaneously:</p>
                      <div className="space-y-2">
                        {[
                          { icon: UserCheck, label: 'Reporting Manager', color: 'orange' },
                          { icon: Building, label: 'HR Department', color: 'purple' },
                          { icon: ShieldCheck, label: 'Administration', color: 'blue' },
                          { icon: DollarSign, label: 'Payroll/Finance', color: 'green' },
                          { icon: Laptop, label: 'IT Department', color: 'cyan' }
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <item.icon className={`w-4 h-4 text-${item.color}-500`} />
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* My History Tab */}
          <TabsContent value="history">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-gray-600" />
                  My Exit Request History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myRequests.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No exit request history found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myRequests.map(request => (
                      <div key={request.id} className="p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{request.reason_for_leaving}</p>
                            <p className="text-sm text-gray-500">
                              Submitted: {formatDate(request.submission_date)} | LWD: {formatDate(request.last_working_day)}
                            </p>
                          </div>
                          {getOverallStatusBadge(request)}
                        </div>
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <Badge variant="outline" className={request.rm_status === 'approved' ? 'bg-green-50' : 'bg-red-50'}>
                            RM: {request.rm_status}
                          </Badge>
                          <Badge variant="outline" className={request.hr_status === 'approved' ? 'bg-green-50' : 'bg-red-50'}>
                            HR: {request.hr_status}
                          </Badge>
                          <Badge variant="outline" className={request.admin_status === 'approved' ? 'bg-green-50' : 'bg-red-50'}>
                            Admin: {request.admin_status}
                          </Badge>
                          <Badge variant="outline" className={request.payroll_status === 'approved' ? 'bg-green-50' : 'bg-red-50'}>
                            Payroll: {request.payroll_status}
                          </Badge>
                          <Badge variant="outline" className={request.it_status === 'approved' ? 'bg-green-50' : 'bg-red-50'}>
                            IT: {request.it_status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Approvals Tab */}
          {pendingApprovals.length > 0 && (
            <TabsContent value="approvals">
              <Card className="shadow-lg border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-800">
                    <AlertTriangle className="w-5 h-5" />
                    Pending Your Approval ({pendingApprovals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {pendingApprovals.map(request => {
                      const clearanceDept = request.clearance_dept;
                      const clearanceComplete = isClearanceComplete(request, clearanceDept);
                      
                      return (
                        <div key={`${request.id}-${request.approval_role}`} className="p-4 bg-white border rounded-lg shadow-sm">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-400" />
                                {request.employee_name}
                              </h4>
                              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                <Briefcase className="w-3 h-3" />
                                {request.department || 'N/A'} • {request.designation || 'N/A'}
                              </p>
                            </div>
                            <Badge className="bg-orange-500">
                              {request.approval_role === 'rm' ? 'Manager Approval' : 
                               request.approval_role === 'hr' ? 'HR Approval' : 
                               request.approval_role === 'admin' ? 'Admin Approval' : 
                               request.approval_role === 'payroll' ? 'Payroll Approval' : 'IT Approval'}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                            <div>
                              <span className="text-gray-500">Reason:</span>
                              <p className="font-medium">{request.reason_for_leaving}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Last Working Day:</span>
                              <p className="font-medium text-red-600">{formatDate(request.last_working_day)}</p>
                            </div>
                          </div>

                          {/* Department Clearance Section */}
                          {clearanceDept && (
                            <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium">Your Clearance ({deptConfig[clearanceDept]?.label})</span>
                                <Badge className={clearanceComplete ? 'bg-green-500' : 'bg-orange-500'}>
                                  {clearanceComplete ? 'Complete' : 'Incomplete'}
                                </Badge>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openClearanceDialog(request, clearanceDept)}
                              >
                                <ClipboardList className="w-3 h-3 mr-1" />
                                Update Clearance
                              </Button>
                            </div>
                          )}

                          <div className="flex gap-2 flex-wrap">
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              disabled={!clearanceComplete}
                              onClick={() => {
                                setSelectedRequest(request);
                                setShowApprovalDialog(true);
                              }}
                              title={!clearanceComplete ? 'Complete clearance before approving' : ''}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => {
                                setSelectedRequest({...request, action: 'reject'});
                                setShowApprovalDialog(true);
                              }}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                            
                            {/* HR Only Actions */}
                            {request.approval_role === 'hr' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setShowInterviewDialog(true);
                                  }}
                                >
                                  <Video className="w-4 h-4 mr-1" /> 
                                  {request.interview_scheduled ? 'View Interview' : 'Schedule Interview'}
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setShowRevokeDialog(true);
                                  }}
                                >
                                  <RotateCcw className="w-4 h-4 mr-1" /> Revoke
                                </Button>
                              </>
                            )}
                          </div>

                          {!clearanceComplete && (
                            <p className="text-xs text-orange-600 mt-2">
                              ⚠️ Complete all clearance items (Yes/No/NA) before you can approve
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Submit Exit Request Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <LogOut className="w-5 h-5" />
              Submit Resignation
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This action will initiate your resignation process. 
                Your notice period is 60 days and cannot be modified.
              </p>
            </div>

            <div>
              <Label>Reason for Leaving *</Label>
              <Select 
                value={exitForm.reason_for_leaving} 
                onValueChange={(val) => setExitForm(prev => ({...prev, reason_for_leaving: val}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {reasonOptions.map(reason => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Additional Details (Optional)</Label>
              <Textarea
                value={exitForm.detailed_reason}
                onChange={(e) => setExitForm(prev => ({...prev, detailed_reason: e.target.value}))}
                placeholder="Provide any additional details..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleSubmitRequest}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Resignation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedRequest?.action === 'reject' ? 'Reject' : 'Approve'} Exit Request
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p><strong>Employee:</strong> {selectedRequest.employee_name}</p>
                <p><strong>Last Working Day:</strong> {formatDate(selectedRequest.last_working_day)}</p>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add any comments..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
              Cancel
            </Button>
            <Button 
              className={selectedRequest?.action === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
              onClick={() => handleApproval(selectedRequest?.action || 'approve')}
            >
              {selectedRequest?.action === 'reject' ? 'Reject' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Dialog (HR Only) */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <RotateCcw className="w-5 h-5" />
              Revoke Exit Request
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  <strong>Warning:</strong> This will cancel the resignation request for {selectedRequest.employee_name}. 
                  Their employment will continue as normal.
                </p>
              </div>

              <div>
                <Label>Reason for Revocation *</Label>
                <Textarea
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Provide reason for revoking..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevokeDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleRevoke}
              disabled={!revokeReason}
            >
              Revoke Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exit Interview Dialog */}
      <Dialog open={showInterviewDialog} onOpenChange={setShowInterviewDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Video className="w-5 h-5" />
              {selectedRequest?.interview_scheduled ? 'Exit Interview Details' : 'Schedule Exit Interview'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p><strong>Employee:</strong> {selectedRequest.employee_name}</p>
                <p><strong>Last Working Day:</strong> {formatDate(selectedRequest.last_working_day)}</p>
              </div>

              {selectedRequest.interview_scheduled ? (
                <>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium mb-2">Scheduled Interview</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><strong>Date:</strong> {selectedRequest.interview_date}</p>
                      <p><strong>Time:</strong> {selectedRequest.interview_time}</p>
                      <p><strong>Mode:</strong> {selectedRequest.interview_mode}</p>
                    </div>
                  </div>

                  {selectedRequest.interview_completed ? (
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> Interview Completed
                      </h4>
                      <p className="text-sm">{selectedRequest.interview_feedback}</p>
                    </div>
                  ) : (
                    <div>
                      <Label>Interview Feedback *</Label>
                      <Textarea
                        value={interviewFeedback}
                        onChange={(e) => setInterviewFeedback(e.target.value)}
                        placeholder="Enter interview feedback and notes..."
                        rows={4}
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Interview Date *</Label>
                      <Input
                        type="date"
                        value={interviewForm.interview_date}
                        onChange={(e) => setInterviewForm(prev => ({...prev, interview_date: e.target.value}))}
                      />
                    </div>
                    <div>
                      <Label>Interview Time *</Label>
                      <Input
                        type="time"
                        value={interviewForm.interview_time}
                        onChange={(e) => setInterviewForm(prev => ({...prev, interview_time: e.target.value}))}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Interview Mode *</Label>
                    <Select 
                      value={interviewForm.interview_mode} 
                      onValueChange={(val) => setInterviewForm(prev => ({...prev, interview_mode: val}))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="In-Person">In-Person</SelectItem>
                        <SelectItem value="Video Call">Video Call</SelectItem>
                        <SelectItem value="Phone">Phone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Location / Meeting Link (Optional)</Label>
                    <Input
                      value={interviewForm.interview_location}
                      onChange={(e) => setInterviewForm(prev => ({...prev, interview_location: e.target.value}))}
                      placeholder="e.g., Conference Room A or Zoom link"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowInterviewDialog(false);
              setInterviewForm({ interview_date: '', interview_time: '', interview_mode: '', interview_location: '' });
              setInterviewFeedback('');
            }}>
              Cancel
            </Button>
            {selectedRequest?.interview_scheduled && !selectedRequest?.interview_completed ? (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleCompleteInterview}
                disabled={!interviewFeedback}
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Mark Complete
              </Button>
            ) : !selectedRequest?.interview_scheduled && (
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleScheduleInterview}
              >
                <Calendar className="w-4 h-4 mr-1" /> Schedule Interview
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clearance Update Dialog */}
      <Dialog open={showClearanceDialog} onOpenChange={setShowClearanceDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600" />
              Update {selectedDept && deptConfig[selectedDept]?.label} Clearance
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && selectedDept && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p><strong>Employee:</strong> {selectedRequest.employee_name}</p>
              </div>

              <div className="space-y-3">
                <Label>Checklist Items (Select Yes/No/NA for each)</Label>
                {clearanceItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="text-sm flex-1">{item.name}</span>
                    <Select
                      value={item.status || ''}
                      onValueChange={(val) => {
                        const updated = [...clearanceItems];
                        updated[idx] = { ...updated[idx], status: val };
                        setClearanceItems(updated);
                      }}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="na">N/A</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={clearanceNotes}
                  onChange={(e) => setClearanceNotes(e.target.value)}
                  placeholder="Add any notes about clearance..."
                  rows={2}
                />
              </div>

              <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                <strong>Note:</strong> All items must be marked (Yes/No/NA) before you can approve the exit request.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowClearanceDialog(false);
              setSelectedDept(null);
              setClearanceItems([]);
              setClearanceNotes('');
            }}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleUpdateClearance}
            >
              Update Clearance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExitPortalPage;
